import {breakpointSizes} from '$theme';
import {ReactNode, useMemo, useState} from 'react';
import {ProductImage} from '../image/product-image/product-image';
import {ModuleCarousel} from '../module-carousel';
import {Overlay} from '../overlay';

import {
    StyledImagePreviewerWrapper,
    StyledImagePreviewLargeImage,
    StyledOverlayContentWrapper,
    StyledOverlayCarouselWrapper,
    StyledImagePreviewLargeOverlay,
    ClickableBackground,
} from './style';
import Button from '$shared/components/button';
import {Icon} from "$shared/components/icon";

export type ImagePreviewerItem<ItemData = unknown> = ItemData & {
    src?: string;
    alt?: string;
    type: 'image' | 'file' | 'x3d' | 'stp';
    name?: string;
    linkedStp?: { name: string; src: string };
};

type ImagePreviewerProps<ItemData> = {
    itemsData?: ImagePreviewerItem<ItemData>[];
    children?: (itemData: ImagePreviewerItem<ItemData>) => ReactNode;
};

export const ImagePreviewer = <T, >({itemsData, children}: ImagePreviewerProps<T>) => {
    const [activeIndex, setActiveIndex] = useState(0);
    const [openPreview, setOpenPreview] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    const activeItem = itemsData?.[activeIndex];

    const handleDownload = (e: React.MouseEvent) => {
        e.stopPropagation();
        if (!activeItem?.src) return;

        setIsLoading(true);

        const link = document.createElement("a");
        link.href = activeItem.src as string;
        link.download = activeItem.src.split("/").pop() || "download";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        setTimeout(() => {
            setIsLoading(false);
        }, 500);
    };

    const previewContent = useMemo(() => {
        if (!activeItem) return null;

        if (activeItem.type === "image") {
            return (
                <ProductImage
                    src={activeItem.src}
                    objectFit="contain"
                    priority={true}
                    key={activeItem.src}
                    sizes={`(max-width: ${breakpointSizes.sm}px) 100vw, 50vw`}
                />
            );
        } else if (activeItem.type === "x3d") {
                    const modelHtml = `
            <!DOCTYPE html>
            <html lang="en">
            <head>
              <meta charset="UTF-8" />
              <title>X3D Viewer</title>
              <script src="https://cdn.jsdelivr.net/npm/x3dom@1.8.3/x3dom.min.js"></script>
              <link rel="stylesheet" href="https://cdn.jsdelivr.net/npm/x3dom@1.8.3/x3dom.min.css" />
              <style>
                html, body {
                  margin: 0;
                  padding: 0;
                  width: 100%;
                  height: 99%;
                  background: #f9f9f9;
                  cursor: grab;
                }
                x3d, canvas {
                  width: 100%;
                  height: 100%;
                  cursor: grab;
                }
              </style>
            </head>
            <body>
              <x3d showStat="false" showLog="false">
                <scene>
                  <navigationInfo type='"EXAMINE" "ANY"' headlight="true"></navigationInfo>
                  <inline url="${activeItem.src}" />
                </scene>
              </x3d>
            </body>
            </html>
          `;
            const linkedStp = (activeItem as any).linkedStp;

            return (
                <div
                    style={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        width: "100%",
                        height: "100%",
                        aspectRatio: "1",
                        background: "#f9f9f9",
                        borderRadius: "10px",
                        textAlign: "center",
                        position: "relative",
                        pointerEvents: "auto",
                    }}
                >
                    <iframe
                        srcDoc={modelHtml}
                        sandbox="allow-scripts"
                        allow="xr-spatial-tracking"
                        style={{
                            width: "100%",
                            height: "100%",
                            overflow: "hidden",
                            border: "none",
                            borderRadius: "10px",
                            pointerEvents: "auto"
                        }}
                    />
                    {linkedStp && (
                        <div style={{
                            position: 'absolute',
                            top: '10px',
                            right: '10px',
                            zIndex: 5,
                            color: 'white',
                            padding: '6px 10px',
                            borderRadius: '8px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '10px',
                        }}>
                            <Button
                                variant="primary"
                                iconLeft={<Icon icon="download" color="white" size={16}/>}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    if (!linkedStp?.src) return;
                                    const link = document.createElement("a");
                                    link.href = linkedStp.src;
                                    link.download = linkedStp.name || "download.stp";
                                    document.body.appendChild(link);
                                    link.click();
                                    document.body.removeChild(link);
                                }}
                            >
                                Download STP
                            </Button>
                        </div>
                    )}
                </div>
            );
        } else {
            return (
                <div
                    style={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        width: "100%",
                        aspectRatio: "1",
                        background: "#f9f9f9",
                        borderRadius: "10px",
                        textAlign: "center",
                        position: "relative",
                    }}
                >
                    {activeItem.type === "file" && (
                        <div style={{position: "relative", zIndex: 2}}>
                            <Button
                                isLoading={isLoading}
                                disabled={isLoading}
                                onClick={handleDownload}
                                onMouseDown={(e) => e.preventDefault()}
                                iconLeft={<Icon icon="download" color="white" size={20}/>}
                                variant="primary"
                            >
                                {isLoading ? "Downloading..." : `Download ` +  activeItem.name?.split('.').pop()?.toUpperCase()}
                            </Button>
                        </div>
                    )}
                </div>
            );
        }
    }, [activeItem, isLoading]);

    const items = useMemo(() =>
            itemsData?.map((item) => children?.(item)),
        [itemsData]
    );

    const moduleCarouselRender = <ModuleCarousel items={items} onItemSelect={setActiveIndex}/>;

    const overlayContent = (
        <StyledOverlayContentWrapper>
            <ClickableBackground onClick={() => setOpenPreview(false)}/>
            <StyledImagePreviewLargeOverlay>{previewContent}</StyledImagePreviewLargeOverlay>
            <StyledOverlayCarouselWrapper>{moduleCarouselRender}</StyledOverlayCarouselWrapper>
        </StyledOverlayContentWrapper>
    );

    return (
        <StyledImagePreviewerWrapper>
            <Overlay
                content={overlayContent}
                fullScreen
                controlled
                open={openPreview}
                onOpenChange={setOpenPreview}
            >
                <StyledImagePreviewLargeImage
                    style={{pointerEvents: 'auto', zIndex: 1}}>{previewContent}</StyledImagePreviewLargeImage>
            </Overlay>
            {moduleCarouselRender}
        </StyledImagePreviewerWrapper>
    );
};